import { useNavigate } from '@tanstack/react-router';
import { Button } from '@volvo/vcdk-react';
import { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import type { IIndustrialForm, IMarineForm, IUnitBaseRegistration } from '@/service/vpc/types/IUnitRegistration';

import { withComponentErrorBoundary } from '@/components/ComponentErrorBoundary';
import Modal from '@/components/modals/AppModal';
import IndustrialContactInformationContent from '@/components/modals/unit-registration/industrial/IndustrialContactInformationContent';
import IndustrialNoUnitAddedContent from '@/components/modals/unit-registration/industrial/IndustrialNoUnitAddedContent';
import IndustrialUnitAddedContent from '@/components/modals/unit-registration/industrial/IndustrialUnitAddedContent';
import IndustrialUnitInformationContent from '@/components/modals/unit-registration/industrial/IndustrialUnitInformationContent';
import MarineUnitAddedContent from '@/components/modals/unit-registration/marine/MarineUnitAddedContent';
import MarineUnitInformationContent from '@/components/modals/unit-registration/marine/MarineUnitInformationContent';
import SearchUnitContent from '@/components/modals/unit-registration/SearchUnitContent';
import { analyticsEvents } from '@/contexts/analytics/events';
import { useFleetAccount } from '@/contexts/fleet-account/useFleetAccount';

export const StepEnum = {
  SEARCH_UNIT: 'SEARCH_UNIT',
  MARINE_UNIT_INFORMATION: 'MARINE_UNIT_INFORMATION',
  MARINE_UNIT_ADDED: 'MARINE_UNIT_ADDED',
  INDUSTRIAL_CONTACT_INFORMATION: 'INDUSTRIAL_CONTACT_INFORMATION',
  INDUSTRIAL_UNIT_INFORMATION: 'INDUSTRIAL_UNIT_INFORMATION',
  INDUSTRIAL_NO_UNIT_ADDED: 'INDUSTRIAL_NO_UNIT_ADDED',
  INDUSTRIAL_UNIT_ADDED: 'INDUSTRIAL_UNIT_ADDED',
} as const;

export type StepEnum = (typeof StepEnum)[keyof typeof StepEnum];

type AddNewUnitModalProps = {
  initialSerialNumber?: string;
  autoOpen?: boolean;
  autoSearch?: boolean;
  origin?: string; // VIP or VPC
  qrCode?: string;
  onClose?: () => void;
};

export type ISegmentData = {
  mainSegment: string;
  segment: string;
  segmentDetail: string;
};

const AddNewUnitModal: React.FC<AddNewUnitModalProps> = ({
  initialSerialNumber,
  autoOpen = false,
  autoSearch = false,
  origin,
  qrCode,
  onClose,
}) => {
  const { t } = useTranslation();
  const { isMaximumClaimedReached } = useFleetAccount();
  const navigate = useNavigate();

  const [isOpen, setIsOpen] = useState<boolean>(autoOpen);
  const [currentStep, setCurrentStep] = useState<StepEnum>(StepEnum.SEARCH_UNIT);
  const [unitNotExistInProductCenter, setUnitNotExistInProductCenter] = useState(false);

  const [addedUnitId, setAddedUnitId] = useState<null | number>(null);
  const [segment, setSegment] = useState<Partial<ISegmentData>>({});
  const [defaultValues, setDefaultValues] = useState<Partial<IUnitBaseRegistration>>({});
  const [defaultValuesMarine, setDefaultValuesMarine] = useState<Partial<IMarineForm>>({});
  const [defaultValuesIndustrial, setDefaultValuesIndustrial] = useState<Partial<IIndustrialForm>>({});

  const resetFormData = () => {
    setCurrentStep(StepEnum.SEARCH_UNIT);
    setUnitNotExistInProductCenter(false);
    setAddedUnitId(null);
    setDefaultValues({});
    setDefaultValuesMarine({});
    setDefaultValuesIndustrial({});
    setSegment({});
  };

  const handleClose = useCallback(() => {
    setIsOpen(false);
    resetFormData();
    onClose?.();
  }, [onClose]);

  const renderStep = () => {
    switch (currentStep) {
      case StepEnum.SEARCH_UNIT:
        return (
          <SearchUnitContent
            initialSerialNumber={initialSerialNumber}
            autoSearch={autoSearch}
            setUnitNotExistInProductCenter={setUnitNotExistInProductCenter}
            setSegment={setSegment}
            setDefaultValues={setDefaultValues}
            setDefaultValuesMarine={setDefaultValuesMarine}
            setDefaultValuesIndustrial={setDefaultValuesIndustrial}
            onCancel={handleClose}
            onNext={setCurrentStep}
          />
        );
      case StepEnum.MARINE_UNIT_INFORMATION:
        return (
          <MarineUnitInformationContent
            segment={segment}
            unitNotExistInProductCenter={unitNotExistInProductCenter}
            defaultValues={defaultValues}
            defaultValuesMarine={defaultValuesMarine}
            onCancel={() => setCurrentStep(StepEnum.SEARCH_UNIT)}
            onNext={(endCustomerUnitId) => {
              setAddedUnitId(endCustomerUnitId);
              setCurrentStep(StepEnum.MARINE_UNIT_ADDED);
            }}
          />
        );
      case StepEnum.MARINE_UNIT_ADDED:
        return (
          <MarineUnitAddedContent
            onCancel={() => setCurrentStep(StepEnum.SEARCH_UNIT)}
            onNext={() => {
              handleClose();
              if (addedUnitId !== null) {
                // Navigate to the newly added unit's details page
                navigate({
                  to: '/units/$endCustomerUnitId/specifications',
                  params: { endCustomerUnitId: String(addedUnitId) },
                });
              }
            }}
          />
        );
      case StepEnum.INDUSTRIAL_CONTACT_INFORMATION:
        return (
          <IndustrialContactInformationContent
            origin={origin}
            qrCode={qrCode}
            segment={segment}
            unitNotExistInProductCenter={unitNotExistInProductCenter}
            defaultValues={defaultValues}
            setDefaultValues={setDefaultValues}
            defaultValuesIndustrial={defaultValuesIndustrial}
            setDefaultValuesIndustrial={setDefaultValuesIndustrial}
            onCancel={() => setCurrentStep(StepEnum.SEARCH_UNIT)}
            onNext={setCurrentStep}
          />
        );
      case StepEnum.INDUSTRIAL_UNIT_INFORMATION:
        return (
          <IndustrialUnitInformationContent
            segment={segment}
            unitNotExistInProductCenter={unitNotExistInProductCenter}
            defaultValues={defaultValues}
            defaultValuesIndustrial={defaultValuesIndustrial}
            onCancel={() => {
              setCurrentStep(StepEnum.INDUSTRIAL_CONTACT_INFORMATION);
            }}
            onNext={(step, unitId) => {
              if (unitId != null) setAddedUnitId(unitId);
              setCurrentStep(step);
            }}
          />
        );
      case StepEnum.INDUSTRIAL_NO_UNIT_ADDED:
        return (
          <IndustrialNoUnitAddedContent
            engineSerials={defaultValues.engineSerials ?? []}
            onCancel={() => setCurrentStep(StepEnum.SEARCH_UNIT)}
            onNext={handleClose}
          />
        );
      case StepEnum.INDUSTRIAL_UNIT_ADDED:
        return (
          <IndustrialUnitAddedContent
            onCancel={() => setCurrentStep(StepEnum.SEARCH_UNIT)}
            onNext={() => {
              handleClose();
              if (addedUnitId !== null) {
                navigate({
                  to: '/units/$endCustomerUnitId/specifications',
                  params: { endCustomerUnitId: String(addedUnitId) },
                });
              }
            }}
          />
        );
      default:
        return <></>;
    }
  };

  return isMaximumClaimedReached ? null : (
    <>
      <Button
        variant="primary"
        size="medium"
        onClick={(e) => {
          e.preventDefault();
          analyticsEvents.Interaction.componentInteraction({
            parent: 'AddNewUnitModal',
            type: 'Button',
            title: 'Open Add New Unit Modal',
            placement: 'AddNewUnitModal',
          });
          setIsOpen(true);
        }}
      >
        {t(($) => $.components.modal.add_new_unit_modal.button)}
      </Button>
      {isOpen &&
        createPortal(
          <Modal open={isOpen} onClose={handleClose} maxWidth="90%" width={700}>
            {renderStep()}
          </Modal>,
          document.body
        )}
    </>
  );
};

export default withComponentErrorBoundary(AddNewUnitModal);
